import {
  AddIcon,
  AlertInfo,
  ArrowLeftIcon,
  ArrowRightIcon,
  EllipsisVerticalIcon,
  IconButton,
  IconButtonSize,
  IconButtonType,
  PrimaryButton,
  TertiaryButton,
  useDialogState,
} from '@tricor-digital-team/vistra-design-system';
import { v4 as uuid } from 'uuid';
import React, { forwardRef, useRef, useState } from 'react';
import IndividualModal from './IndividualModal';
import { useSelector, useDispatch } from 'react-redux';
import {
  addIndividual,
  removeIndividual,
  resetIndividual,
  updateIndividual,
} from '../../../../Redux/individualSlice';
import { useNavigate } from 'react-router-dom';
import CancelStep from '../CancelStep';
import RemoveStep from '../RemoveStep';
import PersonalDetails from './PersonalDetails';
import { z } from 'zod';
import { resetFormData } from '../../../../Redux/formSlice';
import { id } from 'date-fns/locale';

const IndividualDetail = ({ setCurrentStep }) => {
  const fieldRefs = useRef({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cancelDialog = useDialogState({ visible: false });
  const individualDetails = useSelector((state) => state.individual.individualDetails);
  const dialog = useDialogState({ visible: false });
  const [selectedIndividual, setSelectedIndividual] = useState(null);
  const [isError, setIsError] = useState(false);
  const [personalDetailsErrors, setPersonalDetailsErrors] = useState({});

  const personalDetailsSchema = z.object({
    firstName: z
      .string()
      .trim()
      .min(1, 'First name is required'), // trims and ensures non-empty
    lastName: z
      .string()
      .trim()
      .min(1, 'Last name is required'),
    middleName: z
      .string()
      .trim()
      .optional(),
    email: z
      .string()
      .email('Invalid email address')
  });

  const validatePersonalDetails = () => {
    const allErrors = {};
    let isValid = true;

    individualDetails.forEach(({ personalDetails }, index) => {
      const result = personalDetailsSchema.safeParse(personalDetails);

      // Collect Zod errors
      if (!result.success) {
        isValid = false;
        const fieldErrors = result.error.flatten().fieldErrors;
        allErrors[index] = fieldErrors;
      } else {
        allErrors[index] = {};
      }

      // Check uniqueness
      const emailToCheck = personalDetails.email?.toLowerCase()?.trim();
      const isDuplicate = individualDetails.some((item, i) => {
        if (i === index) return false;
        return item.personalDetails.email?.toLowerCase()?.trim() === emailToCheck;
      });

      if (isDuplicate) {
        isValid = false;
        allErrors[index].email = 'Email already exists';
      }
    });

    setPersonalDetailsErrors(allErrors);

    if (!isValid) {
      setIsError(true)
      // Find first field with error
      for (const [idx, fields] of Object.entries(allErrors)) {
        for (const field of Object.keys(fields)) {
          const refKey = `${field}-${idx}`;
          const el = fieldRefs.current[refKey];
          if (el) {
            setTimeout(() => { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus() }, 50);
            return isValid;
          }
        }
      }
    }
    return isValid;
  };
  const addIndividualData = () => {
    setIsError(false);
    const currentIndex = individualDetails.length;
    delete personalDetailsErrors[currentIndex]

    setPersonalDetailsErrors(personalDetailsErrors);
    dispatch(
      addIndividual({
        id: uuid(),
        personalDetails: {
          firstName: '',
          lastName: '',
          email: '',
          middleName: '',
        },
      })
    );
  };

  const removeIndividualData = (id) => {
    setIsError(false)
    const ind = individualDetails.findIndex(ind => ind.id === id);
    delete personalDetailsErrors[ind]
    for (let i = ind; i < individualDetails.length; i++) {
      if (personalDetailsErrors[i + 1]) {
        personalDetailsErrors[i] = personalDetailsErrors[i + 1]
      }
    }

    delete personalDetailsErrors[individualDetails.length - 1]

    setPersonalDetailsErrors(personalDetailsErrors);
    dispatch(removeIndividual(id));
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <div className="text-[#68696C] text-sm">
          You can add up to 10 individuals (Directors, LLP members, Persons with Significant Control (PSC) and individuals who file on behalf of Companies).
          <div className='mt-4'>All fields are mandatory unless labelled as optional.</div>
        </div>
      </div>

      {individualDetails.map((details, index) => (

        <div key={details.id} className={`overflow-x-auto ${index !== 0 && 'border-gray-100 border-t-2'} mt-4`}>
          <PersonalDetails
            fieldRefs={fieldRefs}
            personalDetails={details.personalDetails}
            setPersonalDetails={(fn) =>
              dispatch(
                updateIndividual({
                  index,
                  id: details.id,
                  data: { id: details.id, personalDetails: fn(details.personalDetails) },
                })
              )
            }
            index={index}
            existingEmails={individualDetails?.map(i => i?.personalDetails?.email)}
            removeIndividual={() => removeIndividualData(details.id)}
            personalDetailsErrors={personalDetailsErrors[index] || {}}
            setPersonalDetailsErrors={setPersonalDetailsErrors}
            currentLength={individualDetails.length}
            setIsError={setIsError}
          />
        </div>
      ))}

      {individualDetails.length < 10 ? (<div className="flex justify-center items-center mt-4 border-t-2 border-gray-100 pt-4">
        <TertiaryButton
          icon={AddIcon}
          disabled={individualDetails.length >= 10}
          iconPosition="left"
          label="Add individual detail"
          onClick={addIndividualData}
          size="large"
        />
      </div>) :
        <div className="flex justify-center items-center mt-4 border-t-2 border-gray-100 pt-4">
          <AlertInfo info>
            <div className="text-base">
              <span className="font-bold">You have reached the limit of 10 individuals</span><br />
              If the entity you're supporting has more than ten individuals requiring this service, contact our{' '}
              <a
                href="mailto:bulkeccta@vistra.com"
                className="text-blue-800 underline font-medium"
              >
                support team
              </a>.
            </div>
          </AlertInfo>
        </div>

      }

      <div className="flex justify-between items-center mt-8">
        <TertiaryButton
          label="Cancel"
          onClick={() => cancelDialog.show()}
          size="large"
        />
        <div className="flex justify-between gap-6">
          <TertiaryButton
            icon={ArrowLeftIcon}
            iconPosition="left"
            label="Back"
            onClick={() => setCurrentStep(0)}
            size="large"
          />
          <PrimaryButton
            icon={ArrowRightIcon}
            disabled={isError || individualDetails.length === 0}
            iconPosition="right"
            label="Next"
            onClick={(e) => {
              e.preventDefault();
              if (!validatePersonalDetails()) return;
              if (individualDetails.length === 0) {
                setIsError(true);
              } else {
                setCurrentStep(2);
              }
            }}
            size="large"
          />
        </div>
      </div>
      <CancelStep
        dialog={cancelDialog}
        handleExit={() => {
          dispatch(resetFormData());
          dispatch(resetIndividual());
          cancelDialog.hide();
          navigate('/');
        }}
      />
    </div>
  );
};

export default IndividualDetail;
